import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useRouterState,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { MfaSetupPage } from '../auth/MfaSetupPage';
import { SignInPage } from '../auth/SignInPage';
import { useMe, useSession } from '../auth/session';
import { Beacon } from '../components/feedback/Beacon';
import { TopProgress } from '../components/feedback/Progress';
import { Toaster } from '../components/feedback/toast';
import { AppShell } from '../components/shell/AppShell';
import { ApiError } from '../lib/api';
import { HomePage } from '../pages/HomePage';
import { AppearancePage, NotFoundPage } from '../pages/misc';
import { OrganizationPage } from '../pages/OrganizationPage';
import { PeoplePage } from '../pages/PeoplePage';

// Routes. Guards are components: signed out → sign-in; MFA required and blocking → setup
// screen first (the API would answer 403 mfa_enrollment_required anyway).

function Root() {
  return (
    <>
      <TopProgress />
      <Outlet />
      <Toaster />
    </>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { status, signOut } = useSession();
  const me = useMe(status === 'signedIn');
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (status === 'checking') return <Beacon />;
  if (status === 'signedOut') return <Navigate to="/sign-in" />;
  if (me.isLoading) return <Beacon />;
  if (me.error) {
    // Valid Cognito user without access (not invited, deactivated) or session gone.
    void signOut();
    return <Navigate to="/sign-in" />;
  }
  if (me.data?.mfa.blocking && pathname !== '/security/mfa') return <Navigate to="/security/mfa" />;
  return <>{children}</>;
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useSession();
  if (status === 'checking') return <Beacon />;
  if (status === 'signedIn') return <Navigate to="/" />;
  return <>{children}</>;
}

const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFoundPage });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: () => (
    <GuestOnly>
      <SignInPage />
    </GuestOnly>
  ),
});

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security/mfa',
  component: () => (
    <RequireAuth>
      <MfaSetupPage />
    </RequireAuth>
  ),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: () => (
    <RequireAuth>
      <AppShell />
    </RequireAuth>
  ),
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage });
const orgRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization',
  component: OrganizationPage,
});
const peopleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/people',
  component: PeoplePage,
});
const appearanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/appearance',
  component: AppearancePage,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  mfaRoute,
  appRoute.addChildren([homeRoute, orgRoute, peopleRoute, appearanceRoute]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/** Don't retry what retrying can't fix (4xx); do retry blips (network, 5xx) twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}
